import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import UserApi from "api/userApi";
import dayjs from "dayjs";
import { useAuth } from "auth-context/auth.context";
import ChangePassword from "./changePassword";
import IdentificationField from "./components/identificationField";
import BirthDateField from "./components/birthDateField";
import GenderField from "./components/genderField";
import ProfessionalCardField from "./components/professionalCardField";

const UserInfo = () => {
  const { user } = useAuth();
  const userIdentification = user?.identificacion;
  const groups = user?.groups || [];

  const [userData, setUserData] = useState({
    identificacion: "",
    fecha_nacimiento: "",
    genero: "",
    tarjeta_profesional: "",
    ciudad_documento: "",
    es_operador: false,
  });
  const [originalData, setOriginalData] = useState({
    identificacion: "",
    fecha_nacimiento: "",
    genero: "",
    tarjeta_profesional: "",
    ciudad_documento: "",
    es_operador: false,
  });
  const [editingField, setEditingField] = useState({
    fecha_nacimiento: false,
    genero: false,
    tarjeta_profesional: false,
    ciudad_documento: false,
  });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!userIdentification) {
          console.log("No hay identificación de usuario disponible");
          return;
        }

        const response = await UserApi.fetchUser(userIdentification);
        const { identificacion, fecha_nacimiento, genero, tarjeta_profesional, ciudad_documento } =
          response.data;
        const isOperador = groups && groups.includes("OPERADOR(A)");

        setUserData({
          identificacion: identificacion || "",
          fecha_nacimiento: fecha_nacimiento ? dayjs(fecha_nacimiento).format("DD/MM/YYYY") : "",
          genero: genero === "MA" ? "Masculino" : genero === "FE" ? "Femenino" : "Otro",
          tarjeta_profesional: tarjeta_profesional || "",
          ciudad_documento: ciudad_documento || "",
          es_operador: isOperador,
        });
        setOriginalData({
          identificacion,
          fecha_nacimiento,
          genero,
          tarjeta_profesional,
          ciudad_documento: ciudad_documento || "",
          es_operador: isOperador,
        });
        setError(null);
      } catch (error) {
        console.error("Error al obtener los datos del usuario:", error);
        setError(obtenerMensajeError(error, "Error al cargar los datos del usuario"));
      }
    };

    fetchUserData();
  }, [userIdentification, groups]);

  const handleSave = async (field) => {
    try {
      const updatedData = { ...originalData };
      if (field === "fecha_nacimiento") {
        updatedData.fecha_nacimiento = dayjs(userData.fecha_nacimiento, "DD/MM/YYYY").toISOString();
      } else if (field === "genero") {
        updatedData.genero =
          userData.genero === "Masculino" ? "MA" : userData.genero === "Femenino" ? "FE" : "OT";
      } else if (field === "tarjeta_profesional") {
        updatedData.tarjeta_profesional = userData.tarjeta_profesional;
      } else if (field === "ciudad_documento") {
        updatedData.ciudad_documento = userData.ciudad_documento;
      }

      await UserApi.updateUser(userIdentification, updatedData);
      setOriginalData(updatedData);
      setEditingField((prev) => ({ ...prev, [field]: false }));
      setError(null);
    } catch (error) {
      console.error("Error al guardar los datos del usuario:", error);
      setError(obtenerMensajeError(error, "Error al guardar los cambios"));
    }
  };

  const handleCancel = (field) => {
    if (field === "fecha_nacimiento") {
      setUserData((prev) => ({
        ...prev,
        fecha_nacimiento: originalData.fecha_nacimiento
          ? dayjs(originalData.fecha_nacimiento).format("DD/MM/YYYY")
          : "",
      }));
    } else if (field === "genero") {
      setUserData((prev) => ({
        ...prev,
        genero:
          originalData.genero === "MA"
            ? "Masculino"
            : originalData.genero === "FE"
            ? "Femenino"
            : "Otro",
      }));
    } else {
      setUserData((prev) => ({
        ...prev,
        [field]: originalData[field] || "",
      }));
    }

    setEditingField((prev) => ({ ...prev, [field]: false }));
  };

  const handleToggleChangePassword = () => {
    setShowChangePassword(!showChangePassword);
  };

  const handlePasswordChangeSuccess = () => {
    setShowChangePassword(false);
  };

  if (!user) {
    return null;
  }

  return (
    <Card sx={{ height: "400px" }}>
      <SuiBox display="flex" flexDirection="column" height="100%">
        <SuiBox pt={2} px={2} pb={1}>
          <SuiTypography variant="h6" fontWeight="medium" textTransform="capitalize">
            Información de Usuario
          </SuiTypography>
        </SuiBox>
        <SuiBox px={2} pb={2} display="flex" flexDirection="column" flex={1} overflow="auto">
          {error && (
            <SuiBox mb={2}>
              <SuiTypography variant="caption" color="error" fontWeight="medium">
                {error}
              </SuiTypography>
            </SuiBox>
          )}

          {showChangePassword ? (
            <SuiBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="100%"
              py={3}
            >
              <ChangePassword
                userId={userIdentification}
                onClose={handleToggleChangePassword}
                onSuccess={handlePasswordChangeSuccess}
              />
            </SuiBox>
          ) : (
            <>
              <SuiBox lineHeight={1.25}>
                <IdentificationField
                  identification={userData.identificacion}
                  ciudad={userData.ciudad_documento}
                />

                <BirthDateField
                  value={userData.fecha_nacimiento}
                  isEditing={editingField.fecha_nacimiento}
                  onEdit={() => setEditingField({ ...editingField, fecha_nacimiento: true })}
                  onSave={() => handleSave("fecha_nacimiento")}
                  onCancel={() => handleCancel("fecha_nacimiento")}
                  onChange={(value) =>
                    setUserData((prev) => ({ ...prev, fecha_nacimiento: value }))
                  }
                />

                <GenderField
                  value={userData.genero}
                  isEditing={editingField.genero}
                  onEdit={() => setEditingField({ ...editingField, genero: true })}
                  onSave={() => handleSave("genero")}
                  onCancel={() => handleCancel("genero")}
                  onChange={(value) => setUserData((prev) => ({ ...prev, genero: value }))}
                />

                <ProfessionalCardField
                  value={userData.tarjeta_profesional}
                  isEditing={editingField.tarjeta_profesional}
                  onEdit={() => setEditingField({ ...editingField, tarjeta_profesional: true })}
                  onSave={() => handleSave("tarjeta_profesional")}
                  onCancel={() => handleCancel("tarjeta_profesional")}
                  onChange={(value) =>
                    setUserData((prev) => ({ ...prev, tarjeta_profesional: value }))
                  }
                  isVisible={userData.es_operador}
                />

                <SuiBox display="flex" alignItems="center">
                  <SuiBox width="60%" mr={2}>
                    <SuiButton
                      onClick={handleToggleChangePassword}
                      variant="gradient"
                      buttonColor="dark"
                      size="small"
                    >
                      Cambiar Contraseña
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </>
          )}
        </SuiBox>
      </SuiBox>
    </Card>
  );
};

export default UserInfo;
